import axios from "axios";
/** Custom Services */
import alertService from "../alert/AlertService";
/** Custom Interceptors */
import authenticationInterceptor from "./AuthenticationInterceptor";
import {authenticationRepository} from "./AuthenticationRepository";
import storageUtil from "../../../utils/StorageUtil";
import DataSource from "../../../data/DataSource";
import {environment} from "@/environments/environment";
import AuthenticationRepository
  from "../../../repositories/mifos/AuthenticationRepository";
import OTPRepository from "../../../repositories/creditengine/OTPRepository";
import OAuthTokenRepository from "@/repositories/oauth/OAuthTokenRepository";
import NetworkErrorUtil from "@/utils/NetworkErrorUtil";
import AppuserRepository from "@/repositories/creditengine/AppuserRepository";
import PrefManager from "@/data/model/PrefManager";
import UserRepository from "@/repositories/mifos/UserRepository";
import EzUtils from "@/utils/EzUtils";
import {DataTables} from "@/utils/DataTables";
import SmsRepository from "@/repositories/creditengine/SmsRepository";
import {Constants} from "@/components/Constants";
import LoanScopeDataHolder from "@/data/model/LoanScopeDataHolder";
import UrlParams from "@/data/model/UrlParams";
import EncryptionDecryptionUtils from "@/utils/EncryptionDecryptionUtils";

/**
 * Authentication workflow.
 */
export default {
  name: "authentication",
  data: () => ({
    /** Denotes whether the user credentials should persist through sessions. */
    rememberMe: false,

    /**
     * Denotes the type of storage:
     *
     * Session Storage: User credentials should not persist through sessions.
     *
     * Local Storage: User credentials should persist through sessions.
     *
     * All credentials are stored in local storage using weex storage (Done by @Anish)
     */

    /** User credentials. */
    credentials: null,

    /** Key to store credentials in storage. */
    credentialsStorageKey: "mifosXCredentials",

    /** Key to store oauth token details in storage. */
    oAuthTokenDetailsStorageKey: "mifosXOAuthTokenDetails",

    /** Key to store two factor authentication token in storage. */
    twoFactorAuthenticationTokenStorageKey: "mifosXTwoFactorAuthenticationToken"
  }),
  computed: {
    savedCredentials() {
      return JSON.parse(storageUtil.getItem(this.credentialsStorageKey));
    },
  },

  created() {
    if (this.savedCredentials) {
      if (this.savedCredentials.rememberMe) {
        this.rememberMe = true;
      }

      const twoFactorAccessToken = JSON.parse(
          storageUtil.getItem(this.twoFactorAuthenticationTokenStorageKey));
      if (environment.authenticationType === Constants.AUTH_TYPE_OAUTH) {
        this.refreshOAuthAccessToken();
      } else {
        /** @namespace savedCredentials.base64EncodedAuthenticationKey **/
        authenticationInterceptor.setAuthorizationToken(
            savedCredentials.base64EncodedAuthenticationKey);
      }

      if (twoFactorAccessToken) {
        authenticationInterceptor.setTwoFactorAccessToken(
            twoFactorAccessToken.token);
      }
    }
  },

  generateOTP(phoneNumber) {
    return new Promise(resolve => {
      OTPRepository.generateOTP(phoneNumber)
      .then(res => {
        resolve(DataSource.onResponse(res.data))
      })
      .catch(error => {
        resolve(
            DataSource.onError(NetworkErrorUtil.getNetworkIssueDetails(error)));
      })
    });
  },

  /**
   * Send otp on phoneNumber.
   * @param phoneNumber Phone number.
   */
  sendOtp(phoneNumber) {
    return new Promise(resolve => {
      SmsRepository.sendOtp(phoneNumber)
      .then(res => {
        resolve(DataSource.onResponse(res.data))
      })
      .catch(error => {
        resolve(DataSource.onError(
            NetworkErrorUtil.getNetworkIssueDetails(error)
        ));
      })
    });
  },

  /**
   * Authenticates the user.
   * @param loginContext Login parameters.
   */
  login(loginContext) {
    this.rememberMe = true;
    const loginWithCredentials = loginContext.loginWithCredentials;
    const credentialsParams = loginWithCredentials ?
        {
          username: loginContext.username,
          password: loginContext.password
        }
        : {
          username: loginContext.username,
          firstname: loginContext.username,
          lastname: loginContext.username,
          mobile: loginContext.username,
          isSelfServiceUser: true,
          authData: {
            dataId: loginContext.dataId,
            data: loginContext.password,
            mode: loginContext.mode
          }
        };

    return new Promise(resolve => {
      if (loginWithCredentials) {
        if (environment.authenticationType === Constants.AUTH_TYPE_OAUTH) {
          OAuthTokenRepository.fetchOAuthToken(credentialsParams)
          .then(oauthDataRes => {
            const oauthData = oauthDataRes && oauthDataRes.data
                ? oauthDataRes.data : {};

            if (!this.isValidCredentials(
                credentialsParams.username,
                credentialsParams.password,
                oauthData["encoded_credentials"]
            )) {
              resolve(DataSource.onError(
                  NetworkErrorUtil.getErrorObject(
                      "User Authentication Failed - Invalid username/password")
              ));
              return;
            }

            this.onLoginSuccess(oauthData);

            PrefManager.setOauthData(oauthData);
            UserRepository.getUserWithAccessToken(oauthData["access_token"])
            .then(userRes => {
              const user = userRes.data;
              PrefManager.setUser(user);
              this.getUserStaffFromUser(user)
              .then(userStaffRes => {
                if (userStaffRes.errorOccurred) {
                  resolve(userStaffRes)
                } else {
                  const userStaffConfig = userStaffRes.data;
                  PrefManager.setStaffDetails(userStaffConfig.staff)
                  PrefManager.setStaffDetails(userStaffConfig.staffConfig)
                  resolve(DataSource.onResponse(userStaffConfig));
                }
              })
            })
            .catch(error => {
              resolve(DataSource.onError(
                  NetworkErrorUtil.getNetworkIssueDetails(error)));
            })
          })
          .catch(error => {
            resolve(DataSource.onError(
                NetworkErrorUtil.getNetworkIssueDetails(error)));
          })
        } else {
          AuthenticationRepository.authenticate(credentialsParams)
          .then(res => {
            const user = res.data;
            if (user
                && !this.isValidCredentials(
                    credentialsParams.username,
                    credentialsParams.password,
                    user["encodedCredentials"]
                )) {
              resolve(DataSource.onError(
                  NetworkErrorUtil.getErrorObject(
                      "User Authentication Failed - Invalid username/password")
              ));
              return;
            }

            this.onLoginSuccess(user);
            PrefManager.setUser(user);
            this.getUserStaffFromUser(user)
            .then(userStaffRes => {
              if (userStaffRes.errorOccurred) {
                resolve(userStaffRes)
              } else {
                const userStaffConfig = userStaffRes.data;
                PrefManager.setStaffDetails(userStaffConfig.staff)
                PrefManager.setStaffDetails(userStaffConfig.staffConfig)
                resolve(DataSource.onResponse(userStaffConfig));
              }
            })
          })
          .catch(error => {
            resolve(DataSource.onError(
                NetworkErrorUtil.getNetworkIssueDetails(error)
            ));
          })
        }
      } else {
        AppuserRepository.authenticate(credentialsParams)
        .then(res => {
          const user = res.data;
          this.onLoginSuccess(user);
          PrefManager.setUser(user);
          this.getUserStaffFromUser(user)
          .then(userStaffRes => {
            if (userStaffRes.errorOccurred) {
              resolve(userStaffRes)
            } else {
              const userStaffConfig = userStaffRes.data;
              PrefManager.setStaffDetails(userStaffConfig.staff)
              PrefManager.setStaffDetails(userStaffConfig.staffConfig)
              resolve(DataSource.onResponse(userStaffConfig));
            }
          })
        })
        .catch(error => {
          resolve(DataSource.onError(
              NetworkErrorUtil.getNetworkIssueDetails(error)
          ));
        })
      }
    });
  },

  getUserStaffFromUser(user) {
    const userStaff = {
      user: user,
      staff: null,
      staffConfig: null,
      shouldRenewPassword: user && user.shouldRenewPassword
    };
    return new Promise(resolve => {
      const staffId = user ? user["staffId"] : -1;
      if (userStaff.shouldRenewPassword) {
        resolve(DataSource.onResponse(userStaff));
      } else if (EzUtils.isValidId(staffId)) {
        DataSource.getStaffDetails(user["staffId"])
        .then(staffDetailsRes => {
          if (staffDetailsRes.errorOccurred) {
            resolve(staffDetailsRes)
          } else {
            userStaff.staff = staffDetailsRes.data;
            DataSource.getDataTableInfo(DataTables.STAFF_CONFIG, staffId)
            .then(staffConfigRes => {
              if (staffConfigRes.errorOccurred) {
                resolve(staffConfigRes);
              } else {
                userStaff.staffConfig = EzUtils.isNotEmpty(staffConfigRes.data)
                    ? staffConfigRes.data[0] : null;
                resolve(DataSource.onResponse(userStaff));
              }
            })
          }
        })
      } else {
        resolve(DataSource.onResponse(userStaff));
      }
    })
  },

  isValidCredentials(username, password, encodedCredentials) {
    if (encodedCredentials && username && password) {
      return encodedCredentials === EncryptionDecryptionUtils.hash256(
          username.toLowerCase() + ":" + password
      );
    }

    return true;
  },

  // methods: {
  /**
   * Retrieves the user details after oauth2 authentication.
   *
   * Sets the oauth2 token refresh time.
   * @param tokenResponse OAuth2 Token details.
   */
  getUserDetails(tokenResponse) {
    const httpParams = {
      access_token: tokenResponse.access_token
    };
    /** @namespace tokenResponse.expires_in **/
    this.refreshTokenOnExpiry(tokenResponse.expires_in);
    authenticationRepository
    .get("/userdetails", {params: httpParams})
    .then(response => {
      this.credentials = response.data;
      this.onLoginSuccess(this.credentials);
      /** @namespace credentials.shouldRenewPassword **/
      if (!this.credentials.shouldRenewPassword) {
        storageUtil.setItem(this.oAuthTokenDetailsStorageKey,
            JSON.stringify(tokenResponse));
      }
    })
  },

  /**
   * Sets the oauth2 token to refresh on expiry.
   * @param {number} expiresInTime OAuth2 token expiry time in seconds.
   */
  refreshTokenOnExpiry(expiresInTime) {
    setTimeout(() => this.refreshOAuthAccessToken(), expiresInTime * 1000);
  },

  /**
   * Refreshes the oauth2 authorization token.
   */
  refreshOAuthAccessToken() {
    const oAuthRefreshToken =
        JSON.parse(storageUtil.getItem(
            this.oAuthTokenDetailsStorageKey)).refresh_token;

    authenticationInterceptor.removeAuthorization();
    let httpParams = {
      client_id: "community-app",
      grant_type: "refresh_token",
      client_secret: "123",
      refresh_token: oAuthRefreshToken,
    };

    authenticationRepository
    .post(`${environment.oauthServerUrl}token`, {},
        {params: httpParams})
    .then(response => {
      const {tokenResponse} = response.data;
      storageUtil.setItem(this.oAuthTokenDetailsStorageKey,
          JSON.stringify(tokenResponse));
      authenticationInterceptor.setAuthorizationToken(
          tokenResponse.access_token);
      this.refreshTokenOnExpiry(tokenResponse.expires_in);
      this.credentials = JSON.parse(
          storageUtil.getItem(this.credentialsStorageKey));
      this.credentials.accessToken = tokenResponse.access_token;
      storageUtil.setItem(this.credentialsStorageKey,
          JSON.stringify(this.credentials));
    });
  },

  /**
   * Sets the authorization token followed by one of the following:
   *
   * Sends an alert if two factor authentication is required.
   *
   * Sends an alert if password has expired and requires a reset.
   *
   * Sends an alert on successful login.
   * @param  credentials Authenticated user credentials.
   */
  onLoginSuccess(credentials) {
    if (environment.authenticationType === Constants.AUTH_TYPE_OAUTH) {
      authenticationInterceptor.setAuthorizationToken(
          credentials["access_token"]
      );
    } else {
      authenticationInterceptor.setAuthorizationToken(
          credentials.base64EncodedAuthenticationKey);
    }
    /** @namespace credentials.isTwoFactorAuthenticationRequired **/
    if (credentials.isTwoFactorAuthenticationRequired) {
      this.credentials = credentials;
      alertService.alert({message: "Two Factor Authentication Required"});
    } else {
      if (credentials.shouldRenewPassword) {
        this.credentials = credentials;
        alertService.alert(
            {message: "Your password has expired, please reset your password!"});
      } else {
        this.setCredentials(credentials);
        delete this.credentials;
      }
    }
  },

  /**
   * Logs out the authenticated user and clears the credentials from storage.
   */
  logout() {
    const twoFactorToken =
        JSON.parse(
            storageUtil.getItem(this.twoFactorAuthenticationTokenStorageKey));

    if (twoFactorToken) {
      authenticationRepository.post("/twofactor/invalidate",
          {token: twoFactorToken.token});
      authenticationInterceptor.removeTwoFactorAuthorization();
    }
    authenticationInterceptor.removeAuthorization();
    this.setCredentials();
  },

  /**
   * Logs out the authenticated user and clears the credentials from storage.
   */
  logoutAndClearLocalStorageData() {
    this.logout();
    PrefManager.clearPrefs();
    LoanScopeDataHolder.clearLoanScopeData();
    UrlParams.clearUrlData();
  },

  /**
   * Checks if the two factor access token for authenticated user is valid.
   * @returns {boolean} True if the two factor access token is valid or two factor authentication is not required.
   */
  twoFactorAccessTokenIsValid() {
    const twoFactorAccessToken =
        JSON.parse(
            storageUtil.getItem(this.twoFactorAuthenticationTokenStorageKey));

    if (twoFactorAccessToken) {
      /** @namespace twoFactorAccessToken.validTo **/
      return ((new Date()).getTime() < twoFactorAccessToken.validTo);
    }
    return true;
  },

  /**
   * Checks if the user is authenticated.
   * @returns {boolean} True if the user is authenticated.
   */
  isAuthenticated() {
    return !!(JSON.parse(storageUtil.getItem(this.credentialsStorageKey))
        && this.twoFactorAccessTokenIsValid());
  },

  /**
   * Gets the user credentials.
   * @returns The user credentials if the user is authenticated otherwise null.
   */
  getCredentials() {
    return JSON.parse(storageUtil.getItem(this.credentialsStorageKey));
  },

  /**
   * Sets the user credentials.
   *
   * The credentials may be persisted across sessions by setting the `rememberMe` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   *
   * @param credentials Authenticated user credentials.
   */
  setCredentials(credentials) {
    if (credentials) {
      credentials.rememberMe = this.rememberMe;
      storageUtil.setItem(this.credentialsStorageKey,
          JSON.stringify(credentials));
    } else {
      storageUtil.removeItem(this.credentialsStorageKey);
      storageUtil.removeItem(this.oAuthTokenDetailsStorageKey);
      storageUtil.removeItem(this.twoFactorAuthenticationTokenStorageKey);
    }
  },

  /**
   * Following functions are for two factor authentication and require
   * first level authorization headers to be setup for the requests.
   */

  /**
   * Gets the two factor authentication delivery methods available for the user.
   */
  getDeliveryMethods() {
    return authenticationRepository.get("/twofactor");
  },

  /**
   * Requests OTP to be sent via the given delivery method.
   * @param deliveryMethod Delivery method for the OTP.
   */
  requestOTP(deliveryMethod) {
    let httpParams = {
      /** @namespace deliveryMethod.name **/
      deliveryMethod: deliveryMethod.name,
      extendedToken: this.rememberMe.toString()
    };

    return authenticationRepository.post(`/twofactor`, {},
        {params: httpParams});
  },

  /**
   * Validates the OTP and authenticates the user on success.
   * @param {string} otp
   */
  validateOTP(otp) {
    const httpParams = {
      token: otp
    };
    return authenticationRepository
    .post(`/twofactor/validate`, {}, {params: httpParams})
    .then(response => {
      this.onOTPValidateSuccess(response.data);
    });
  },

  /**
   * Sets the two factor authorization token followed by one of the following:
   *
   * Sends an alert if password has expired and requires a reset.
   *
   * Sends an alert on successful login.
   * @param response Two factor authentication token details.
   */
  onOTPValidateSuccess(response) {
    /** @namespace response.token **/
    authenticationInterceptor.setTwoFactorAccessToken(response.token);
    if (this.credentials.shouldRenewPassword) {
      alertService.alert({
        type: "Password Expired",
        message: "Your password has expired, please reset your password!"
      });
    } else {
      this.setCredentials(this.credentials);
      alertService.alert({
        type: "Authentication Success",
        message: `${this.credentials.username} successfully logged in!`
      });
      delete this.credentials;
      storageUtil.setItem(this.twoFactorAuthenticationTokenStorageKey,
          JSON.stringify(response));
    }
  },

  /**
   * Resets the user"s password and authenticates the user.
   * @param passwordDetails New password.
   */
  resetPassword(passwordDetails) {
    /** @namespace credentials.userId **/
    return authenticationRepository
    .put(`/users/${this.credentials.userId}`, passwordDetails)
    .then(() => {
      alertService.alert({
        type: "Password Reset Success",
        message: `Your password was sucessfully reset!`
      });
      authenticationInterceptor.removeAuthorization();
      authenticationInterceptor.removeTwoFactorAuthorization();
      const loginContext = {
        username: this.credentials.username,
        password: passwordDetails.password,
        remember: this.rememberMe
      };
      // this.login(loginContext).subscribe();
    });
  }
  // },
};

export const LoginMode = {
  FIREBASE: "FIREBASE",
  OTP_SMS: "OTP_SMS",
  OUTBOUND_CALL: "OUTBOUND_CALL",
  GOOGLE_AUTH: "GOOGLE_AUTH",
  PARTNER_AUTH: "PARTNER_AUTH"
};
